import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useLazyGetPaymentStatusQuery } from "@/lib/api/apiSlice";
import { PaymentAttemptStatus } from "muminsmart-utils";
import { routes } from "@/lib/constants";
import PaymentStatusSkeleton from "@/app/components/common/skeletons/PaymentStatusSkeleton";

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const paymentAttemptId = searchParams.get("paymentAttemptId") ?? undefined;
  const orderId = searchParams.get("orderId") ?? undefined;
  const [view, setView] = useState<
    "loading" | "missing" | "failed" | "pending" | "checking"
  >("loading");
  const [fetchPaymentStatus] = useLazyGetPaymentStatusQuery();

  useEffect(() => {
    if (!paymentAttemptId || !orderId) {
      setView("missing");
      return;
    }

    void fetchPaymentStatus({ paymentAttemptId, orderId })
      .unwrap()
      .then((data) => {
        if (data.status === PaymentAttemptStatus.Verified) {
          navigate(routes.orders.index);
          return;
        }

        if (
          data.status === PaymentAttemptStatus.Failed ||
          data.status === PaymentAttemptStatus.Released
        ) {
          setView("failed");
          return;
        }

        setView("pending");
      })
      .catch(() => {
        setView("checking");
      });
  }, [fetchPaymentStatus, orderId, paymentAttemptId, navigate]);

  if (view === "loading") {
    return <PaymentStatusSkeleton />;
  }

  if (view === "missing") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="rounded-lg bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-semibold text-gray-900">
            Payment could not be completed
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Payment details were not found. Please try again or contact support
            if the issue persists.
          </p>
          <Link
            to={routes.cart}
            className="mt-4 inline-block rounded-full bg-primary px-4 py-2 text-white"
          >
            Return to Cart
          </Link>
        </div>
      </div>
    );
  }

  if (view === "failed") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="rounded-lg bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-semibold text-gray-900">
            Payment could not be completed
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Your payment attempt was not completed. Please try again from the
            cart.
          </p>
          <Link
            to={routes.cart}
            className="mt-4 inline-block rounded-full bg-primary px-4 py-2 text-white"
            replace={true}
          >
            Return to Cart
          </Link>
        </div>
      </div>
    );
  }

  if (view === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="rounded-lg bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-semibold text-gray-900">
            Payment status is being checked
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Your payment has been submitted. Refreshing this page will only
            check the payment status and will not create another order.
          </p>
          <div className="mt-4 flex flex-col justify-center gap-2">
            <Link
              to={routes.cart}
              className="rounded-full bg-secondary px-4 py-2 text-white"
              replace={true}
            >
              Return to Cart
            </Link>
            <Link
              to={routes.orders.index}
              className="rounded-full bg-primary px-4 py-2 text-white"
              replace={true}
            >
              View Orders
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="rounded-lg bg-white p-8 text-center shadow-sm">
        <h1 className="text-xl font-semibold text-gray-900">
          Payment is being processed
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          We are waiting for the Payment Gateway to confirm your payment. You
          can refresh this page to check the payment status.
        </p>
        <div className="mt-4 flex justify-center gap-2">
          <Link
            to={routes.cart}
            className="mt-4 inline-block rounded-full bg-primary px-4 py-2 text-white"
            replace={true}
          >
            Return to Cart
          </Link>
          <Link
            to={routes.orders.index}
            className="mt-4 inline-block rounded-full bg-primary px-4 py-2 text-white"
            replace={true}
          >
            View Orders
          </Link>
        </div>
      </div>
    </div>
  );
}
